/**
 * Shared progressive enhancement for ET product, offer and journal rows.
 * Native overflow/scroll snapping remains usable without JavaScript. Each region
 * owns its controls and cleanup; the singleton also supports legacy quiz pages.
 * Reimagined rows explicitly disable autoplay. Only legacy rows that request it
 * install its pause/resume listeners. No framework, cloned slides or polling.
 */
(() => {
  'use strict';
  if (window.ETNativeCarousel) return;

  const controllers = new Set();
  const byRegion = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Connect one server-rendered region. Repeated editor loads remain harmless. */
  function createController(region) {
    if (!region) return null;
    if (byRegion.has(region)) {
      const existing = byRegion.get(region);
      existing.refresh();
      return existing;
    }
    const track = region.querySelector('.native-carousel-scroll');
    if (!track) return null;

    const previous = region.querySelector('.native-scroll-prev');
    const next = region.querySelector('.native-scroll-next');
    const navigation = region.querySelector('[data-etr-navigation]');
    const position = region.querySelector('[data-etr-position]');
    const positionLabel = region.querySelector('[data-etr-position-label]');
    const autoSeconds = Number.parseInt(track.dataset.autoScroll, 10) || 0;
    const listeners = new AbortController();
    const on = (node, type, callback, options = {}) => {
      node?.addEventListener(type, callback, { ...options, signal: listeners.signal });
    };
    let frame = null;
    let timer = null;
    let needsMeasure = true;
    let destroyed = false;
    let geometry = { step: 240, visible: 1, total: 0, maximum: 0 };

    for (const button of [previous, next]) {
      if (button && track.id) button.setAttribute('aria-controls', track.id);
    }

    /** Measure on initialisation/resize, not on every touch-scroll frame. */
    function measure() {
      const slides = track.querySelectorAll('.native-carousel-slide');
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = Math.max(1, (slides[0]?.getBoundingClientRect().width || 240) + gap);
      geometry = {
        step,
        total: slides.length,
        visible: Math.max(1, Math.floor((track.clientWidth + gap + 1) / step)),
        maximum: Math.max(0, track.scrollWidth - track.clientWidth),
      };
      needsMeasure = false;
    }

    /** Keep boundary buttons and the optional translated range in sync. */
    function update() {
      if (destroyed) return;
      if (needsMeasure) measure();
      // Clamp Safari overscroll before deriving the visible range.
      const offset = Math.max(0, Math.min(geometry.maximum, track.scrollLeft));
      const atStart = offset <= 1;
      const atEnd = offset >= geometry.maximum - 1;
      for (const [button, disabled] of [[previous, atStart], [next, atEnd]]) {
        if (!button) continue;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      }
      if (!navigation) return;
      navigation.hidden = geometry.maximum <= 1;
      region.classList.add('etr-carousel-enhanced');
      const first = Math.min(geometry.total, Math.round(offset / geometry.step) + 1);
      const last = atEnd ? geometry.total : Math.min(geometry.total, first + geometry.visible - 1);
      if (position) {
        position.textContent = first === last
          ? `${first} / ${geometry.total}` : `${first}–${last} / ${geometry.total}`;
      }
      if (positionLabel?.dataset.template) {
        positionLabel.textContent = positionLabel.dataset.template
          .replace('[first]', first).replace('[last]', last).replace('[total]', geometry.total);
      }
    }

    /** Coalesce scroll events; resizing invalidates the cached card geometry. */
    function scheduleUpdate() {
      if (frame !== null || destroyed) return;
      frame = window.requestAnimationFrame(() => { frame = null; update(); });
    }
    function refresh() { needsMeasure = true; scheduleUpdate(); }

    /** Move a single card, respecting the shopper's current motion preference. */
    function advance(direction) {
      if (needsMeasure) measure();
      track.scrollBy({ left: geometry.step * direction, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    function stopAuto() {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    }
    function startAuto() {
      stopAuto();
      if (autoSeconds <= 0 || reduceMotion.matches || document.hidden || destroyed) return;
      timer = window.setInterval(() => {
        if (region.matches(':hover, :focus-within')) return;
        if (needsMeasure) measure();
        if (geometry.maximum <= 1) return;
        if (track.scrollLeft >= geometry.maximum - 1) track.scrollTo({ left: 0, behavior: 'smooth' });
        else advance(1);
      }, autoSeconds * 1000);
    }

    on(previous, 'click', () => { stopAuto(); advance(-1); });
    on(next, 'click', () => { stopAuto(); advance(1); });
    on(track, 'scroll', scheduleUpdate, { passive: true });
    on(track, 'keydown', (event) => {
      // Arrow keys inside editable controls belong to those controls, not the row.
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
        || event.target.closest('input, textarea, select, [contenteditable]')) return;
      const button = event.key === 'ArrowRight' ? next : event.key === 'ArrowLeft' ? previous : null;
      if (button && !button.disabled) { event.preventDefault(); button.click(); }
    });

    // Observe the actual track/card dimensions, including Theme Editor sidebars
    // and late stylesheet application. Older browsers retain a resize fallback.
    const resize = window.ResizeObserver ? new window.ResizeObserver(refresh) : null;
    if (resize) {
      resize.observe(track);
      const firstSlide = track.querySelector('.native-carousel-slide');
      if (firstSlide) resize.observe(firstSlide);
    } else on(window, 'resize', refresh, { passive: true });

    if (autoSeconds > 0) {
      on(region, 'mouseenter', stopAuto);
      on(region, 'mouseleave', startAuto);
      on(region, 'focusin', stopAuto);
      on(region, 'focusout', startAuto);
      on(region, 'pointerdown', stopAuto);
      on(window, 'pointerup', startAuto);
      on(document, 'visibilitychange', startAuto);
      on(reduceMotion, 'change', startAuto);
    }

    const controller = {
      region,
      refresh,
      /** Cancel all owned work and restore native scrolling on editor removal. */
      destroy() {
        destroyed = true;
        stopAuto();
        if (frame !== null) window.cancelAnimationFrame(frame);
        listeners.abort();
        resize?.disconnect();
        region.classList.remove('etr-carousel-enhanced');
        if (navigation) navigation.hidden = true;
        controllers.delete(controller);
        byRegion.delete(region);
      },
    };
    byRegion.set(region, controller);
    controllers.add(controller);
    update();
    if (autoSeconds > 0) startAuto();
    return controller;
  }

  /** Find regions inside either a whole document or an injected editor section. */
  function init(root = document) {
    if (root.matches?.('.et-nt-carousel-region')) createController(root);
    root.querySelectorAll?.('.et-nt-carousel-region').forEach(createController);
  }
  function destroy(root) {
    [...controllers].forEach((controller) => {
      if (controller.region === root || root.contains(controller.region)) controller.destroy();
    });
  }

  window.ETNativeCarousel = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
